//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

// Status enum for CustomFunction
export enum CustomActionPythonStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

// Supported data types for InputVariable
export enum InputVariableDataType {
  STRING = 'String',
  BOOLEAN = 'Boolean',
  DATE = 'Date',
  DATETIME = 'Datetime',
  DOUBLE = 'Double',
  INTEGER = 'Integer',
  OBJECT = 'Object',
  PASSWORD = 'Password',
}

// InputVariable interface
export interface InputVariable {
  id?: string;
  functionId?: string;
  name: string;
  displayName: string;
  helpText?: string;
  dataType: InputVariableDataType | string;
  multivalued?: boolean;
  required?: boolean;
}

export interface InputVariableServerPayload extends Omit<InputVariable, 'multivalued' | 'required'> {
  multivalued?: boolean;
  required?: boolean;
}

export interface InputVariablePayload extends Omit<InputVariable, 'multivalued' | 'required'> {
  multivalued?: string;
  required?: string;
}

export interface CustomFunctionTest {
  id?: string;
  functionId?: string;
  lastExecuted?: string; // timestamp
  lastExecutionTime?: number; // Integer (milliseconds)
  inputData?: Record<string, any>; // JSONB
  lastOutput?: Record<string, any>; // JSONB
  expectedReturnValue?: Record<string, any> | null; // JSONB, default null
  success?: boolean;
  errorMessage?: string | null;
}

export interface CustomActionPython<TInputVariable = InputVariablePayload> {
  id?: string;
  displayName: string;
  apiName: string;
  description?: string;
  helpText?: string;
  code?: string;
  requirements?: string;
  status?: CustomActionPythonStatus | string;
  tags?: string[];
  iconPath?: string;
  lastExecutedAt?: string; // timestamp
  inputVariables?: TInputVariable[];
  tests?: CustomFunctionTest[];
}

export interface CustomActionPythonPayload extends CustomActionPython<InputVariablePayload> {
  shareWithOrg?: boolean;
  shareGlobally?: boolean;
}

export interface CustomActionPythonServerPayload extends CustomActionPython<InputVariableServerPayload> {
  shareWithOrg?: boolean;
  shareGlobally?: boolean;
}

export interface GeneratedTestCaseVariableValue {
  name?: string;
  displayName?: string;
  dataType?: string;
  required?: string;
  value?: string;
}

export interface GeneratedTestCase {
  name: string;
  description?: string;
  variableValues: GeneratedTestCaseVariableValue[];
}

export interface CustomActionPythonSyncAIGeneratedPayload {
  displayName?: string;
  apiName?: string;
  helpText?: string;
  code?: string;
  requirements?: string;
  inputVariables?: InputVariablePayload[];
  testCases?: GeneratedTestCase[];
}

export interface CustomActionPythonSyncAIGeneratedServerPayload
  extends Omit<CustomActionPythonSyncAIGeneratedPayload, 'inputVariables'> {
  inputVariables?: InputVariableServerPayload[];
}

export interface CreateSyncAISessionResponse {
  sessionId: string;
  status?: string;
  provider?: string;
  timestamp?: string;
}

export interface SendSyncAIMessageRequest {
  sessionId: string;
  message: string;
  currentDefinition?: CustomActionPythonSyncAIGeneratedPayload;
}

export interface SendSyncAIMessageResponse {
  generated?: CustomActionPythonSyncAIGeneratedPayload;
  summary?: string;
  operation?: 'GENERATE' | 'EXPLAIN' | 'CLARIFICATION' | string;
}

export interface CustomActionPythonState {
  customActionPythonWizardVisible: boolean;
}

export type SaveCustomActionPythonResponse = any;
export interface SaveCustomActionPythonRejected {
  message?: string;
}
